import { ImagesHeaderSearchSvg } from '@/assets/images_header.assets';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { HeaderSearchProps } from '@/types/props/components/layout';
import { HeaderEnum } from '@/types/translations/HeaderEnum';
import { createSearchUrl } from '@/utilites/algolia/create-algolia-urls';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { forwardRef, useEffect } from 'react';
import { useSearchBox, useHits } from 'react-instantsearch';
import SpiritoryInput from '../common/Forms/SpiritoryInput';
// --- Analytics imports ---
import { useAnalytics } from '@/hooks/useAnalytics';
import { useSession } from '@/context/SessionContext';
import gTagConstants from '@/constants/gEvenets.constants';
import type { SkuDTO } from '@/types/api/sku';
import useEvents from '@/hooks/useEvents';

const HeaderSearch = forwardRef<HTMLDivElement, HeaderSearchProps>(
  (
    {
      setSearchOnFocus,
      searchOnFocus,
      searchValue: query,
      setSearchValue: setQuery,
      mobile = false,
    },
    ref
  ) => {
    const { translate } = useTypedTranslation<HeaderEnum>('header');
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const { refine } = useSearchBox();
    const { items } = useHits<SkuDTO>();
    const searchQueryParam = searchParams?.get('search');
    const { fireSearchBarFilterFilledEvent } = useEvents(query, items);
    const analytics = useAnalytics();
    const session = useSession();

    function fireSearchBarFilledEvent() {
      const params = {
        batch_ordering_id: analytics.getNextBatchOrderingId(),
        batch_page_id: analytics.getBatchPageId(),
        ga_session_number: analytics.getGASessionNumber(),
        ga_session_id: analytics.getGASessionId(),
        page_location: analytics.pageParams.page_location,
        page_referrer: analytics.pageParams.page_referrer,
        page_title: analytics.pageParams.page_title,
        engagement_time_sec: 0, // Could be improved with timer
        device_category: analytics.getDeviceCategory(),
        session_id: session.sessionToken || '',
        search_term: query,
        results_shown: items.length,
      };
      if (typeof window !== 'undefined') {
        if (window.gtag) {
          window.gtag('event', gTagConstants.eventKeys.search_bar_filled, params);
        } else if (window.dataLayer) {
          window.dataLayer.push({ event: gTagConstants.eventKeys.search_bar_filled, ...params });
        }
      }
    }

    function handleSearchSubmit(e: React.FormEvent<HTMLFormElement>) {
      e.preventDefault();
      fireSearchBarFilterFilledEvent();
      fireSearchBarFilledEvent();
      router.push(createSearchUrl(query));
      setSearchOnFocus(false);
    }

    useEffect(() => {
      if (searchQueryParam) {
        setQuery(searchQueryParam);
        refine(searchQueryParam);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQueryParam]);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        const refVariable = ref as React.MutableRefObject<HTMLDivElement>;
        if (
          refVariable &&
          refVariable.current &&
          !refVariable.current.contains(event.target as Node)
        ) {
          setSearchOnFocus(false);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);

      // Limpiar el evento cuando el componente se desmonte
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [setSearchOnFocus, ref]);

    useEffect(() => {
      setSearchOnFocus(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    useEffect(() => {
      function blockHeight() {
        if (searchOnFocus) {
          document.body.style.overflow = 'hidden';
          document.body.style.height = '100vh';
        } else {
          document.body.style.overflow = '';
          document.body.style.height = '';
        }
      }

      if (mobile && searchOnFocus && query?.trim().length > 0) {
        blockHeight();
      }

      return () => {
        document.body.style.overflow = '';
        document.body.style.height = '';
      };
    }, [searchOnFocus, mobile, query]);

    return (
      <form className="tw-relative tw-w-full tw-pb-3 tw-pt-4" onSubmit={handleSearchSubmit}>
        <SpiritoryInput
          type="text"
          name="search"
          value={query}
          id="search"
          placeholder={translate(HeaderEnum['search_on_spiritory'])}
          onFocus={() => setSearchOnFocus(true)}
          onBlur={() => setSearchOnFocus(false)}
          onChange={e => {
            setSearchOnFocus(true);
            setQuery(e.target.value);
            refine(e.target.value);
          }}
          rightIcon={<Image src={ImagesHeaderSearchSvg} alt="search" width={18} height={18} />}
          autoComplete="off"
        />
      </form>
    );
  }
);

HeaderSearch.displayName = 'HeaderSearch';

export default HeaderSearch;
